<div class="widget">
  @let isWsOpen = isWsOpen$ | async;
  @let threadStatus = threadStatus$ | async;
  @let isCurrentUserThreadParticipant = isCurrentUserThreadParticipant$ | async;
  @let isMine = isMine$ | async;
  @if (!isWsOpen) {
    <div class="ws-error">
      <alg-error
        i18n-message message="Connection with the forum lost. Check your connection, or reload the app if it does not recover automatically."
        icon="ph-duotone ph-wifi-slash"
      ></alg-error>
    </div>
  } @else {
    @let currentState = state();
    @if (currentState.isError) {
      <div class="widget-error">
        <alg-error
          i18n-message message="Unable to load the thread messages."
          icon="ph-duotone ph-warning-circle"
        ></alg-error>
      </div>
    } @else if (currentState.isFetching && !currentState.data) {
      <div class="widget-loading">
        <alg-loading></alg-loading>
        <div class="loading-text" i18n>Loading...</div>
      </div>
    } @else {
      <div class="widget-body">
          @let followStatus = followStatus$ | async;
          @let threadIdForIndicator = threadId$ | async;
          @if (!isMine && threadIdForIndicator && threadStatus?.data?.open) {
            @if (participantUser$ | async; as participantUser) {
              @if (participantUser.name) {
                <alg-thread-top-indicator
                  [followStatus]="followStatus!"
                  [isThreadOpen]="!!threadStatus?.data?.open"
                  [layout]="indicatorLayout()"
                  (followChanged)="onFollowChanged(threadIdForIndicator, $event)"
                ></alg-thread-top-indicator>
              }
            }
          }
          @let showTopIndicator = !isMine && threadIdForIndicator && threadStatus?.data?.open && (participantUser$ | async)?.name;
          <div class="widget-scroll" [class.with-top-indicator]="showTopIndicator" [class.with-top-indicator-below-actions]="showTopIndicator && indicatorLayout() === 'inline-with-back'" #messagesScroll *ngrxLet="{ id: threadId$ | async, userCache: userCache$ | async, canCurrentUserLoadAnswers: canCurrentUserLoadAnswers$ | async } as threadMetadata">
            @if (isCurrentUserThreadParticipant && threadStatus?.isReady && currentState.data?.length === 0) {
              <div class="no-messages" i18n>
              In this panel, you can request help from other users about the content you are on.
              People who have already validated this activity will see your request and be able to answer.
            </div>
            } @else {
              @if (threadMetadata.id) {
                @for (event of currentState.data; track event.time) {
                  <alg-thread-message
                    class="thread-message"
                    [threadId]="threadMetadata.id"
                    [event]="event"
                    [userCache]="threadMetadata.userCache ?? []"
                    [canCurrentUserLoadAnswers]="threadMetadata.canCurrentUserLoadAnswers ?? false"
                    [itemRoute]="threadMetadata.id ? ({ id: threadMetadata.id.itemId, type: 'Task' } | itemRoute) : undefined"
                  ></alg-thread-message>
                }
                @if (currentState.isFetching && currentState.data) {
                  @if (isLoadingActivities() && !hasActivitiesData() && hasMessagesData()) {
                    <div class="refetching-indicator" i18n>Loading the activities...</div>
                  } @else if (isLoadingMessages() && !hasMessagesData() && hasActivitiesData()) {
                    <div class="refetching-indicator" i18n>Loading the messages...</div>
                  } @else if (isLoadingActivities() || isLoadingMessages()) {
                    <div class="refetching-indicator" i18n>Looking for new events...</div>
                  }
                }
              }
            }
            @if (threadMetadata.id; as threadId) {
              <div class="footer-panel" *ngrxLet="!!(threadStatus && !!threadStatus.data && !!threadStatus.data.open) as isThreadOpened">
                <form class="send-form" [formGroup]="form" (ngSubmit)="sendMessage(threadId, isThreadOpened)">
                  <textarea
                    class="textarea"
                    placeholder="Send a message..."
                    formControlName="messageToSend"
                    [rows]="1"
                    algAutoResize
                    (keydown.enter)="$event.preventDefault(); sendMessage(threadId, isThreadOpened)"
                    #messageToSendEl
                  ></textarea>
                  <button
                    class="size-l with-bg send-button"
                    type="submit"
                    icon="ph ph-paper-plane-tilt"
                    alg-button-icon
                    [disabled]="!form.get('messageToSend')?.value?.trim() || !!(disableControls$ | async)"
                  ></button>
                </form>
                <div class="thread-status">
                  <div class="thread-status-caption" >
                    @if (threadStatus?.data?.open) {
                      <span i18n>This thread is open</span>
                    }
                    @if (threadStatus?.data?.open === false) {
                      <span i18n>This thread is closed</span>
                    }
                    @if (threadStatus?.isFetching) {
                      <span i18n>Loading thread status...</span>
                    }
                    @if (threadStatus?.isError) {
                      <span i18n>Error while loading thread info</span>
                    }
                    @if (threadStatus === undefined) {
                      <span i18n>Could not load thread</span>
                    }
                  </div>
                  <div
                    *ngrxLet="!!threadStatus && !!threadStatus.data && ((threadStatus.data.open && threadStatus.data.canClose) || (!threadStatus.data.open && threadStatus.data.canOpen)) as canSwitch"
                    [algTooltip]="threadStatusTooltip"
                    [tooltipDisabled]="canSwitch"
                    tooltipPosition="top"
                    >
                    @if (threadStatus && threadStatus.data) {
                      <button
                        type="button"
                        [disabled]="!canSwitch"
                        alg-button
                        (click)="changeThreadStatus({ open: !threadStatus.data.open, threadId: threadId })"
                        >
                        @if (threadStatus.data.open) {
                          <span i18n>Close this thread</span>
                        } @else {
                          <span i18n>Open this thread</span>
                        }
                      </button>
                    }
                  </div>
                  <ng-template #threadStatusTooltip>
                    @if (threadStatus?.data?.open) {
                      <span i18n>
                      Only the user of the thread can close it.
                    </span>
                    } @else {
                      <span i18n>You are not allowed to open this thread.</span>
                    }
                  </ng-template>
                </div>
              </div>
            }
          </div>
        </div>
      }
  }
</div>

